import React, { useEffect } from 'react';
import { TextField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import {
    LicensePlateField as LicensePlateFieldType,
    FormValues,
    FormConfig
} from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getMinMaxLength } from '../../helpers/getMinMaxLength';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const LicensePlateField = ({ elementId, caption, placeholder, helpText }: LicensePlateFieldType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = LicensePlateField.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = LicensePlateField.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);
    const { minLength, maxLength } = getMinMaxLength(formConfig, elementId);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <TextField
                value={value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setFormValue(elementId, value);
                }}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                isValid={isValid}
                errorText={errorMessage}
                id={elementId}
                key={elementId}
                label={placeholder}
                helpText={helpText}
                required={isRequiredField}
                aria-label={caption}
                data-testid="licenseplatefield"
                minLength={minLength}
                maxLength={maxLength}
            />
        </>
    );
};

LicensePlateField.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as LicensePlateFieldType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);
    console.log('value', value);
    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value} />;
};

LicensePlateField.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    label: string
): ClaimFormSubmitFieldValue => {
    const valueObj = LicensePlateField.getValue(formValues, elementId);
    const valueStr = LicensePlateField.getValueHuman(formValues, elementId);

    // bugfix 2024.12.12: valueObj can be undefined (e.g. if an optional counterpart license plate is not filled out), causing "Cannot read properties of undefined" error
    const valueId = { string: valueObj ? valueObj.replace(/\s+/g, '').toLocaleUpperCase() : '' };

    return getClaimFormSubmitValue('licenseplatefield', elementId, label, valueStr, valueId);
};

LicensePlateField.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

LicensePlateField.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = LicensePlateField.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

LicensePlateField.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const valueObj = LicensePlateField.getValue(formValues, elementId);
    const valueStr = valueObj;

    // bugfix 2024.12.12: valueStr can be undefined (e.g. if an optional counterpart license plate is not filled out), causing "Cannot read properties of undefined" error
    return valueStr ? valueStr.replace(/\s+/g, '').toLocaleUpperCase() : '';
};

LicensePlateField.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

LicensePlateField.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    const isValid = LicensePlateField.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default LicensePlateField;
